import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BrainCircuit,
  BriefcaseBusiness,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  ExternalLink,
  Eye,
  FileSearch,
  Layers3,
  LoaderCircle,
  LockKeyhole,
  Megaphone,
  Play,
  Radar,
  RefreshCcw,
  Rocket,
  SearchCheck,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
} from 'lucide-react'
import { FormEvent, useMemo, useState } from 'react'
import { ventureProducts, ventureRadarWorkflowCode, type VentureProduct } from '../../data/ventureProducts'
import { useDemoStore } from '../../store'
import type { WorkflowRunItem } from '../../types'
import { DemoNotice, PageTitle, StatusBadge } from './shared'

const productIcons = {
  'intent-radar': Radar,
  'revenue-agent': CircleDollarSign,
  'visibility-engine': SearchCheck,
  'content-multiplier': Megaphone,
  'business-os': Layers3,
  'career-os': BriefcaseBusiness,
} as const

const statusLabels = {
  'executable-model': ['Executable model', 'good'],
  'gated-pilot': ['Gated pilot', 'warn'],
  'research-mode': ['Research mode', 'neutral'],
} as const

function latestRunFor(product: VentureProduct, runs: WorkflowRunItem[]) {
  return runs.find(run => run.workflowCode === product.workflowCode)
}

function runTone(status: WorkflowRunItem['status']) {
  if (status === 'completed') return 'good'
  if (status === 'failed' || status === 'cancelled' || status === 'blocked') return 'danger'
  if (status === 'awaiting-approval') return 'warn'
  return 'neutral'
}

export function VentureStudioPage() {
  const { apiMode, runs, workflows, launchWorkflow, refreshLiveData } = useDemoStore()
  const [selectedId, setSelectedId] = useState(ventureProducts[0].id)
  const [objective, setObjective] = useState(ventureProducts[0].defaultObjective)
  const [desiredOutcome, setDesiredOutcome] = useState(ventureProducts[0].defaultOutcome)
  const [priority, setPriority] = useState<'normal' | 'high' | 'urgent'>('high')
  const [launching, setLaunching] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const selected = ventureProducts.find(product => product.id === selectedId) ?? ventureProducts[0]
  const selectedRun = latestRunFor(selected, runs)
  const workflowIndex = useMemo(() => new Map(workflows.map(workflow => [workflow.id, workflow])), [workflows])
  const selectedWorkflow = workflowIndex.get(selected.workflowCode)
  const enabledByControlPlane = selectedWorkflow?.status === 'ready'
  const launchable = apiMode === 'live' && enabledByControlPlane

  const selectProduct = (product: VentureProduct) => {
    setSelectedId(product.id)
    setObjective(product.defaultObjective)
    setDesiredOutcome(product.defaultOutcome)
    setMessage(null)
  }

  const launch = async (event: FormEvent) => {
    event.preventDefault()
    if (!launchable) {
      setMessage(apiMode !== 'live'
        ? 'Start the local DAWN control plane and Hermes bridge to record an executable run.'
        : 'This product remains gated in the control plane until its required sources and acceptance checks are approved.')
      return
    }

    setLaunching(selected.workflowCode)
    setMessage(null)
    try {
      const run = await launchWorkflow(selected.workflowCode, {
        objective,
        desiredOutcome,
        priority,
        approvalPolicy: 'DAWN review required before any external use',
        owner: 'Venture Studio Council',
        submittedFrom: 'venture-studio',
        notes: `Product model: ${selected.name}. External actions are not requested. Use only approved sources and return a decision-ready model output.`,
      }, 120)
      setMessage(`Run ${run.status}. Open the approval queue when Hermes requests a tool permission or final-output review.`)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The model could not be queued.')
    } finally {
      setLaunching(null)
    }
  }

  const launchRadar = async () => {
    const radarWorkflow = workflowIndex.get(ventureRadarWorkflowCode)
    if (apiMode !== 'live' || radarWorkflow?.status !== 'ready') {
      setMessage('The Venture Radar can run after the local control plane is connected and its read-only workflow is enabled.')
      return
    }
    setLaunching(ventureRadarWorkflowCode)
    setMessage(null)
    try {
      await launchWorkflow(ventureRadarWorkflowCode, {
        objective: 'Review approved SaaS marketplace and public market signals, rank new opportunities and assess their impact on the six-product DAWN Venture Studio portfolio.',
        desiredOutcome: 'Produce the daily venture brief, escalation list and build, acquire, partner or monitor recommendations with evidence and confidence.',
        priority: 'high',
        approvalPolicy: 'Internal intelligence output; approval required before converting findings into product claims or external action',
        owner: 'HERMES Venture Radar Agent',
        submittedFrom: 'venture-studio',
        notes: 'Report into the registered SaaS Venture Opportunities daily intelligence feed. Do not copy proprietary code, branding, private data or distinctive protected assets.',
      }, 90)
      setMessage('Venture Radar queued. Its output will be reviewed through DAWN and routed into the daily intelligence briefing feed.')
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The Venture Radar could not be queued.')
    } finally {
      setLaunching(null)
    }
  }

  return (
    <>
      <PageTitle
        title="Venture Studio"
        copy="Six reusable DAWN product models, one governed execution layer and one evidence-led opportunity radar."
        actions={<button className="button button--outline" onClick={() => void launchRadar()} disabled={launching !== null}><Radar size={16} /> {launching === ventureRadarWorkflowCode ? 'Running radar…' : 'Run Venture Radar'}</button>}
      />
      <DemoNotice />

      <section className="venture-hero">
        <div>
          <span className="eyebrow"><Rocket size={14} /> Portfolio build programme</span>
          <h2>Build once. Reuse the governed core. Prove every commercial claim.</h2>
          <p>The models below share DAWN’s Business Brain, approvals, source controls, audit history, budgets, evidence capture and private Hermes execution. Each product adds a specialised operating playbook rather than another disconnected application.</p>
        </div>
        <div className="venture-hero__metrics">
          <article><strong>6</strong><span>product models</span></article>
          <article><strong>7</strong><span>Hermes workflows</span></article>
          <article><strong>0</strong><span>unapproved external actions</span></article>
        </div>
      </section>

      <div className="venture-layout">
        <aside className="venture-products" aria-label="Venture Studio products">
          <div className="panel-heading"><div><h2>Product portfolio</h2><p>Select a model to inspect and run.</p></div></div>
          {ventureProducts.map(product => {
            const Icon = productIcons[product.id as keyof typeof productIcons] ?? Sparkles
            const run = latestRunFor(product, runs)
            const status = statusLabels[product.status]
            const registered = workflowIndex.has(product.workflowCode)
            return (
              <button key={product.id} className={selected.id === product.id ? 'active' : ''} onClick={() => selectProduct(product)}>
                <span className="venture-product-icon"><Icon size={18} /></span>
                <span className="venture-product-copy">
                  <strong>{product.shortName}</strong>
                  <small>{registered ? status[0] : 'Migration pending'}</small>
                </span>
                {run ? <span className={`venture-run-dot venture-run-dot--${runTone(run.status)}`} title={run.status} /> : <ArrowRight size={15} />}
              </button>
            )
          })}
        </aside>

        <main className="venture-detail">
          <header className="venture-detail__head">
            <div>
              <span className="eyebrow">{selected.audience}</span>
              <h2>{selected.name}</h2>
              <p>{selected.promise}</p>
            </div>
            <StatusBadge tone={statusLabels[selected.status][1]}>{statusLabels[selected.status][0]}</StatusBadge>
          </header>

          <div className="venture-thesis">
            <BrainCircuit size={22} />
            <div><strong>DAWN enhancement thesis</strong><p>{selected.enhancement}</p></div>
          </div>

          <div className="venture-spec-grid">
            <article>
              <h3><FileSearch size={16} /> Inputs</h3>
              <ul>{selected.inputs.map(item => <li key={item}>{item}</li>)}</ul>
            </article>
            <article>
              <h3><BadgeCheck size={16} /> Outputs</h3>
              <ul>{selected.outputs.map(item => <li key={item}>{item}</li>)}</ul>
            </article>
            <article>
              <h3><Layers3 size={16} /> Existing stack reused</h3>
              <ul>{selected.sharedCapabilities.map(item => <li key={item}>{item}</li>)}</ul>
            </article>
            <article>
              <h3><BarChart3 size={16} /> Evidence metrics</h3>
              <ul>{selected.successMetrics.map(item => <li key={item}>{item}</li>)}</ul>
            </article>
          </div>

          <form className="venture-launch" onSubmit={launch}>
            <div className="panel-heading">
              <div><h2>Run this model</h2><p>Hermes receives the specialist playbook, approved source manifest and DAWN safety contract.</p></div>
              {selectedWorkflow ? <StatusBadge tone={enabledByControlPlane ? 'good' : 'warn'}>{enabledByControlPlane ? 'Control plane ready' : 'Readiness gate'}</StatusBadge> : <StatusBadge tone="danger">Migration not applied</StatusBadge>}
            </div>
            <label>
              Objective
              <textarea value={objective} onChange={event => setObjective(event.target.value)} rows={3} />
            </label>
            <label>
              Required outcome
              <textarea value={desiredOutcome} onChange={event => setDesiredOutcome(event.target.value)} rows={3} />
            </label>
            <div className="venture-launch__row">
              <label>
                Priority
                <select value={priority} onChange={event => setPriority(event.target.value as typeof priority)}>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
              <div className="venture-safety"><ShieldCheck size={18} /><span><strong>Read and prepare only</strong><small>No send, publish, spend, deployment or destructive action.</small></span></div>
            </div>
            <div className="venture-launch__actions">
              <button className="button button--gold" type="submit" disabled={launching !== null || !objective.trim()}>{launching === selected.workflowCode ? <LoaderCircle className="spin" size={16} /> : <Play size={16} />} Launch governed model</button>
              <button className="button button--outline" type="button" onClick={() => void refreshLiveData()}><RefreshCcw size={15} /> Refresh state</button>
            </div>
            {message ? <p className="venture-message">{message}</p> : null}
          </form>

          <section className="venture-run-panel">
            <div className="panel-heading"><div><h2>Latest model evidence</h2><p>Runtime state is stored against the governed workflow run.</p></div></div>
            {selectedRun ? (
              <div className="venture-run-summary">
                <div><StatusBadge tone={runTone(selectedRun.status)}>{selectedRun.status}</StatusBadge><strong>{selectedRun.workflowName}</strong><small>{new Date(selectedRun.createdAt).toLocaleString('en-GB')}</small></div>
                <div><Clock3 size={16} /><span>{selectedRun.estimatedManualMinutes} min manual baseline</span></div>
                <div><Eye size={16} /><span>{selectedRun.approvalCount ?? 0} approval event(s)</span></div>
                <div><Target size={16} /><span>{selectedRun.agentStatus || 'Hermes state pending'}</span></div>
              </div>
            ) : (
              <div className="venture-empty"><LockKeyhole size={24} /><div><strong>No recorded run yet</strong><p>Apply the Venture Studio migration, connect the local control plane and launch the model to create auditable evidence.</p></div></div>
            )}
          </section>
        </main>
      </div>

      <section className="venture-operating-model">
        <article><span>01</span><div><strong>Venture Radar validates demand</strong><p>Revenue, growth, buyer pain, product movement and strategic fit are assessed with source dates and confidence.</p></div></article>
        <article><span>02</span><div><strong>Specialist council builds the model</strong><p>Research, product, engineering, commercial, risk and QA agents work from one bounded product contract.</p></div></article>
        <article><span>03</span><div><strong>DAWN controls execution</strong><p>Sources, budgets, permissions, approvals, tenant boundaries and audit evidence remain central.</p></div></article>
        <article><span>04</span><div><strong>Revenue evidence decides expansion</strong><p>Only validated usage, conversion, retention and operating evidence justify further product investment.</p></div></article>
      </section>
    </>
  )
}
